export type TourStatus =
  | "not_started"
  | "in_progress"
  | "completed"
  | "dismissed";

export type TourState = {
  status: TourStatus;
  step: number;
};

export type PlatformTourStep = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  hint: string;
  /**
   * Plan 435 M3 (mfh-tour-repoint). A CSS selector (the block's own
   * `data-testid`) the step is ABOUT — never rendered as a spotlight by
   * this module today, but asserted against a real Home render
   * (`platform-tour.test.ts`) so a future block rename can't silently
   * orphan a step. Re-pointed from the retired score/verified/path copy
   * at the hero / offers teaser / agent preview / Prepare row (design
   * §4; roadmap mfh-tour-repoint).
   */
  target: string;
};

/**
 * Code-review fix (M3 iteration 2, finding 5). Step presence by hero/block
 * state — `offers`/`agents` are NOT guaranteed to exist for every settled
 * Home render (`OffersTeaser`/`AgentPreview` both return `null` when their
 * underlying list is empty: `live_quiet` has zero offers, `unpublished_none`
 * has zero browse agents), so the tour must never point a step at a target
 * that isn't actually on the page. `hero` and `prepare` render on every
 * settled Home, so they're always present.
 */
export interface TourStepPresence {
  offersPresent: boolean;
  agentsPresent: boolean;
}

/**
 * The subset of `PLATFORM_TOUR_STEPS` that actually resolve on the current
 * Home render, given which optional blocks are present — order-preserving.
 * `PlatformTourController` walks ONLY this list, so a step whose target
 * block is absent is skipped entirely rather than rendered pointing at
 * nothing.
 */
export function presentTourSteps(
  presence: TourStepPresence,
): readonly PlatformTourStep[] {
  return PLATFORM_TOUR_STEPS.filter((step) => {
    if (step.id === "offers") return presence.offersPresent;
    if (step.id === "agents") return presence.agentsPresent;
    return true;
  });
}

export const PLATFORM_TOUR_STEPS: readonly PlatformTourStep[] = [
  {
    id: "hero",
    eyebrow: "Step 1",
    title: "Your passport is the headline",
    body: "Your tier and readiness score travel with you. Agents compete for the passport shown at the top of Home.",
    hint: "Your passport is the headline",
    target: '[data-testid="home-hero"]',
  },
  {
    id: "offers",
    eyebrow: "Step 2",
    title: "Offers land right below",
    body: "Once agents respond, their incentive offers show up here first — before you have to go looking for them.",
    hint: "Offers land right below the hero",
    target: '[data-testid="home-offers-teaser"]',
  },
  {
    id: "agents",
    eyebrow: "Step 3",
    title: "See who's competing for you",
    body: "Browse the agents with a standing offer at your tier, ranked by estimated savings.",
    hint: "See who's competing for you",
    target: '[data-testid="home-agent-preview"]',
  },
  {
    id: "prepare",
    eyebrow: "Step 4",
    title: "Prepare stays one scroll away",
    body: "Your readiness tools — calculators, rate watch, documents — are always available below the market.",
    hint: "Prepare stays one scroll away",
    target: '[data-testid="home-prepare-row"]',
  },
] as const;

const STORAGE_KEY = "sill.platform_tour.v1";
const SESSION_DISMISS_KEY = "sill.platform_tour.session_dismiss";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function clampStep(step: unknown): number {
  const numeric = typeof step === "number" ? Math.floor(step) : 0;
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Math.min(numeric, PLATFORM_TOUR_STEPS.length - 1);
}

export function isTourStatus(value: unknown): value is TourStatus {
  return (
    value === "not_started" ||
    value === "in_progress" ||
    value === "completed" ||
    value === "dismissed"
  );
}

export function loadTourState(storageKey = STORAGE_KEY): TourState | null {
  if (!isBrowser()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    const status = (parsed as { status?: unknown }).status;
    if (!isTourStatus(status)) {
      return null;
    }
    return {
      status,
      step: clampStep((parsed as { step?: unknown }).step),
    };
  } catch {
    return null;
  }
}

export function saveTourState(state: TourState, storageKey = STORAGE_KEY): void {
  if (!isBrowser()) {
    return;
  }
  window.localStorage.setItem(
    storageKey,
    JSON.stringify({ status: state.status, step: clampStep(state.step) }),
  );
}

export function clearTourState(storageKey = STORAGE_KEY): void {
  if (!isBrowser()) {
    return;
  }
  window.localStorage.removeItem(storageKey);
}

export function isTourSessionDismissed(
  storageKey = SESSION_DISMISS_KEY,
): boolean {
  if (!isBrowser()) {
    return false;
  }
  return window.sessionStorage.getItem(storageKey) === "1";
}

export function markTourSessionDismissed(
  storageKey = SESSION_DISMISS_KEY,
): void {
  if (!isBrowser()) {
    return;
  }
  window.sessionStorage.setItem(storageKey, "1");
}

export function clearTourSessionDismiss(
  storageKey = SESSION_DISMISS_KEY,
): void {
  if (!isBrowser()) {
    return;
  }
  window.sessionStorage.removeItem(storageKey);
}
